/**
 * PageProfile - Modern Discord-style profile page
 * 
 * Complete profile page with all user information, skills, awards, and social links
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { ProfileData } from '../services/profileService';
import { SkillsService, Skill } from '../services/skillsService';
import { AwardsService, Award } from '../services/awardsService';
import { ProjectsService, Project } from '../services/projectsService';
import { OrdersService, Order } from '../services/ordersService';
import { useSubscriptionPermissions } from '../hooks/useSubscriptionPermissions';
import {
  Calendar,
  MapPin,
  Globe,
  Mail,
  Phone,
  Github,
  Twitter,
  Linkedin,
  Instagram,
  MessageCircle,
  Youtube,
  Award as AwardIcon,
  Code,
  Settings,
  User,
  Trophy,
  FolderKanban,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronRight,
  Loader2,
  X
} from 'lucide-react';
import {
  AdminBadgeIcon,
  SubscriberBadgeIcon,
  PartnerBadgeIcon,
  PrivateBadgeIcon,
} from '../components/icons/ProfileBadges';

interface PageProfileProps {
  username?: string;
  userId?: string;
}

interface UserProfile extends ProfileData {
  id: string;
  user_id: string;
  username: string;
  created_at?: string;
  instagram_url?: string;
  youtube_url?: string;
  tiktok_url?: string;
}

const PageProfile: React.FC<PageProfileProps> = ({ username, userId }) => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [awards, setAwards] = useState<Award[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isPartner, setIsPartner] = useState(false);
  const [subscriptionStartDate, setSubscriptionStartDate] = useState<string | null>(null);
  const [isProfilePublic, setIsProfilePublic] = useState<boolean>(true);
  const [visibilityBusyMap, setVisibilityBusyMap] = useState<Record<string, boolean>>({});
  const [isPortfolioCollapsed, setIsPortfolioCollapsed] = useState(false);
  const [selectedPortfolioItem, setSelectedPortfolioItem] = useState<PortfolioItem | null>(null);
  
  // Get subscription info for current user viewing their own profile
  const { subscription: currentUserSubscription, isAdmin: currentUserIsAdmin } = useSubscriptionPermissions();

  useEffect(() => {
    const fetchProfile = async () => {
      if (!supabase) {
        setError('Database not configured');
        setLoading(false);
        return;
      }

      // If neither username nor userId is provided, use the logged-in user
      const targetUserId = userId || (currentUser && !username ? currentUser.id : null);

      if (!username && !targetUserId) {
        setError('Username or user ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch profile by username or user_id
        let profileData;
        let fetchError;
        
        if (username) {
          // Load by username
          const result = await supabase
            .from('user_profiles')
            .select('*')
            .eq('username', username)
            .maybeSingle();
          profileData = result.data;
          fetchError = result.error;
        } else if (targetUserId) {
          // Load by user_id (for logged-in user without username)
          const result = await supabase
            .from('user_profiles')
            .select('*')
            .eq('user_id', targetUserId)
            .maybeSingle();
          profileData = result.data;
          fetchError = result.error;
        }

        if (fetchError) {
          console.error('Error fetching profile:', fetchError);
          
          if (fetchError.code === 'PGRST406') {
            setError('Access denied. You may not have permission to view this profile.');
          } else if (fetchError.code === 'PGRST116') {
            setError('Profile not found');
          } else {
            setError(fetchError.message || 'Failed to load profile');
          }
          setProfile(null);
          return;
        }

        let finalProfile: UserProfile | null = null;
        
        if (!profileData) {
          // If no profile found and we're using user_id, create a minimal profile
          if (targetUserId && targetUserId === currentUser?.id) {
            // Create a minimal profile for the logged-in user
            finalProfile = {
              id: currentUser.id,
              user_id: currentUser.id,
              username: currentUser.email?.split('@')[0] || `user-${currentUser.id.slice(0, 8)}`,
              full_name: currentUser.user_metadata?.full_name || currentUser.email?.split('@')[0] || 'User',
              email: currentUser.email || '',
              show_email: false,
              show_phone: false,
              created_at: currentUser.created_at || new Date().toISOString(),
              is_profile_public: true,
            } as UserProfile;
          } else {
            setError('Profile not found');
            setProfile(null);
            setLoading(false);
            return;
          }
        } else {
          finalProfile = profileData as UserProfile;
        }

        if (!finalProfile) {
          setError('Profile not found');
          setProfile(null);
          setLoading(false);
          return;
        }

        if (finalProfile && typeof finalProfile.is_profile_public !== 'boolean') {
          (finalProfile as UserProfile).is_profile_public = true;
        }

        setProfile(finalProfile);
        setIsProfilePublic(finalProfile.is_profile_public !== false);

        // Fetch admin status, partner status, and subscription info
        const profileUserId = finalProfile.user_id;
        if (!profileUserId) {
          setLoading(false);
          return;
        }

        const viewingOwnProfile = profileUserId === currentUser?.id;

        // Check admin and partner status
        try {
          let profileInfo: { role?: string; is_admin?: boolean; is_partner?: boolean } | null = null;
          let profileInfoError: any = null;

          const { data: profileInfoData, error: profileInfoErr } = await supabase
            .from('user_profiles')
            .select('role, is_admin')
            .eq('user_id', profileUserId)
            .maybeSingle();

          if (profileInfoErr) {
            profileInfoError = profileInfoErr;
          } else {
            profileInfo = profileInfoData;
          }

          if (profileInfoError) {
            console.error('Error fetching profile info for badges:', profileInfoError);
          }

          if (profileInfo) {
            const userIsAdmin = profileInfo.is_admin === true || profileInfo.role === 'Admin' || profileInfo.role === 'Moderator';
            setIsAdmin(userIsAdmin);
            setIsPartner(false);
            console.log('Badge info:', { 
              is_admin: profileInfo.is_admin, 
              role: profileInfo.role, 
              is_partner: false,
              finalIsAdmin: userIsAdmin 
            });
          } else {
            // If viewing own profile, also check from currentUserIsAdmin
            if (profileUserId === currentUser?.id) {
              setIsAdmin(currentUserIsAdmin || false);
            }
            setIsPartner(false);
          }

          // Get subscription start date
          const { data: subscriptionData } = await supabase
            .from('user_subscriptions')
            .select('start_date')
            .eq('user_id', profileUserId)
            .eq('status', 'active')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (subscriptionData?.start_date) {
            setSubscriptionStartDate(subscriptionData.start_date);
          }
        } catch (err) {
          console.error('Error fetching profile badges info:', err);
        }
        
        try {
          const [skillsData, awardsData, projectsData, ordersData] = await Promise.all([
            SkillsService.getPublicSkills(profileUserId).catch(() => []),
            AwardsService.getPublicAwards(profileUserId).catch(() => []),
            (viewingOwnProfile
              ? ProjectsService.getUserProjects(profileUserId)
              : ProjectsService.getUserPublicProjects(profileUserId)
            ).catch((err) => {
              console.error('Error fetching public projects:', err);
              return [];
            }),
            (viewingOwnProfile
              ? OrdersService.getUserOrders(profileUserId, 100)
              : OrdersService.getPublicOrdersForUser(profileUserId)
            ).catch((err) => {
              console.error('Error fetching orders for profile:', err);
              return [];
            }),
          ]);

          setSkills(skillsData || []);
          setAwards(awardsData || []);
          setProjects(projectsData || []);
          setOrders(ordersData || []);
          console.log('✅ Profile data loaded:', {
            skills: skillsData?.length || 0,
            awards: awardsData?.length || 0,
            projects: projectsData?.length || 0
          });
        } catch (fetchError) {
          console.error('Error fetching profile data:', fetchError);
          setSkills([]);
          setAwards([]);
          setProjects([]);
        }

      } catch (err) {
        console.error('Exception fetching profile:', err);
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [username, userId, currentUser?.id]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-[#050b1a]">
        <div className="text-center space-y-4">
          <div className="mx-auto h-14 w-14 animate-spin rounded-full border-2 border-[#7d86ff] border-t-transparent" />
          <p className="text-sm tracking-[0.3em] uppercase text-slate-400">Loading profile…</p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-[#050b1a]">
        <div className="rounded-3xl border border-slate-700/40 bg-slate-900/70 px-10 py-12 text-center shadow-[0_35px_80px_-50px_rgba(15,23,42,0.8)]">
          <h2 className="mb-3 text-2xl font-semibold text-white">Profile not found</h2>
          <p className="max-w-sm text-sm text-slate-300">
            {error || 'The requested profile could not be found or is not accessible.'}
          </p>
        </div>
      </div>
    );
  }

  // Guard against undefined profile
  if (!profile && !loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[rgb(var(--card))]">
        <div className="text-center">
          <p className="text-lg font-semibold text-[rgb(var(--fg))] mb-2">Profile not found</p>
          <p className="text-sm text-[rgb(var(--muted))]">The profile you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  const isOwnProfile = profile ? currentUser?.id === profile.user_id : false;
  const hasNoUsername = isOwnProfile && profile && (!profile.username || profile.username.startsWith('user-'));
  const avatarUrl = profile?.avatar_url || (isOwnProfile ? currentUser?.user_metadata?.avatar_url || null : null);

  // Determine which subscription info to use
  const activeSubscription = isOwnProfile ? currentUserSubscription : null;
  const hasActiveSubscription = activeSubscription?.status === 'active' || (subscriptionStartDate !== null);
  const subscriptionDate = subscriptionStartDate || (activeSubscription ? (activeSubscription as any).start_date : null);

  // Format time since subscription
  const getTimeSinceSubscription = () => {
    if (!subscriptionDate) return '';
    const start = new Date(subscriptionDate);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffYears > 0) {
      return `${diffYears} ${diffYears === 1 ? 'year' : 'years'}`;
    } else if (diffMonths > 0) {
      return `${diffMonths} ${diffMonths === 1 ? 'month' : 'months'}`;
    } else {
      return `${diffDays} ${diffDays === 1 ? 'day' : 'days'}`;
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'expert':
        return 'bg-emerald-500/15 border border-emerald-500/40 text-emerald-600 dark:text-emerald-300';
      case 'advanced':
        return 'bg-indigo-500/15 border border-indigo-500/40 text-indigo-600 dark:text-indigo-300';
      case 'intermediate':
        return 'bg-amber-500/15 border border-amber-500/40 text-amber-600 dark:text-amber-300';
      case 'beginner':
        return 'bg-slate-500/15 border border-slate-500/30 text-slate-600 dark:text-slate-200';
      default:
        return 'bg-slate-500/15 border border-slate-500/30 text-slate-600 dark:text-slate-200';
    }
  };

  const socialLinks = [
    { icon: Github, url: profile.github_url ? `https://github.com/${profile.github_url}` : null, label: 'GitHub', color: 'hover:text-[rgb(var(--fg))]' },
    { icon: Twitter, url: profile.twitter_url, label: 'Twitter', color: 'hover:text-[#1DA1F2]' },
    { icon: Linkedin, url: profile.linkedin_url, label: 'LinkedIn', color: 'hover:text-[#0077B5]' },
    { icon: Instagram, url: profile.instagram_url, label: 'Instagram', color: 'hover:text-[#E4405F]' },
    { icon: MessageCircle, url: profile.discord_username ? `https://discord.com/users/${profile.discord_username}` : null, label: 'Discord', color: 'hover:text-[#5865F2]' },
    { icon: Youtube, url: profile.youtube_url, label: 'YouTube', color: 'hover:text-[#FF0000]' },
    { icon: Globe, url: profile.website, label: 'Website', color: 'hover:text-[rgb(var(--fg))]' },
  ].filter(link => link.url);

  type PortfolioItem = {
    id: string;
    type: 'project' | 'order';
    title: string;
    description?: string | null;
    createdAt: string;
    isPublic: boolean;
    tags?: string[] | null;
    status?: Order['status'];
    budget?: number | null;
    currency?: string | null;
    dueDate?: string | null;
  };

  const formatPortfolioDate = (value: string) =>
    new Date(value).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const getOrderStatusMeta = (status?: Order['status']) => {
    switch (status) {
      case 'completed':
        return { label: 'Completed', dot: 'bg-emerald-500', className: 'text-emerald-500 dark:text-emerald-300' };
      case 'in_progress':
        return { label: 'In progress', dot: 'bg-indigo-500', className: 'text-indigo-500 dark:text-indigo-300' };
      case 'pending':
        return { label: 'Pending', dot: 'bg-amber-500', className: 'text-amber-500 dark:text-amber-300' };
      case 'cancelled':
        return { label: 'Cancelled', dot: 'bg-rose-500', className: 'text-rose-500 dark:text-rose-300' };
      default:
        return { label: 'Unknown status', dot: 'bg-slate-400', className: 'theme-text-tertiary' };
    }
  };

  const formatBudgetValue = (budget?: number | null, currency?: string | null) => {
    if (budget === undefined || budget === null) return null;
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        maximumFractionDigits: 0,
      }).format(budget);
    } catch {
      return `${budget} ${currency || ''}`.trim();
    }
  };

  const canViewPortfolio = profile ? (isOwnProfile || isProfilePublic) : false;

  const portfolioItems: PortfolioItem[] = canViewPortfolio
    ? [
        ...projects
          .filter((project) => isOwnProfile || project.is_public)
          .map((project) => ({
            id: project.id,
            type: 'project' as const,
            title: project.title,
            description: project.description,
            createdAt: project.created_at,
            isPublic: project.is_public,
            tags: project.tags,
          })),
        ...orders
          .filter((order) => isOwnProfile || order.is_public)
          .map((order) => ({
            id: order.id,
            type: 'order' as const,
            title: order.title,
            description: order.description,
            createdAt: order.created_at,
            isPublic: order.is_public,
            status: order.status,
            budget: order.budget ?? null,
            currency: order.currency,
            dueDate: order.due_date ?? null,
          })),
      ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    : [];

  const publicProjectsCount = projects.filter((project) => project.is_public).length;
  const totalProjectsCount = projects.length;
  const publicOrdersCount = orders.filter((order) => order.is_public).length;
  const selectedItemKey = selectedPortfolioItem ? `${selectedPortfolioItem.type}:${selectedPortfolioItem.id}` : null;
  const selectedItemBusy = selectedItemKey ? Boolean(visibilityBusyMap[selectedItemKey]) : false;
  const selectedOrderMeta =
    selectedPortfolioItem?.type === 'order' ? getOrderStatusMeta(selectedPortfolioItem.status) : null;
  const selectedOrderBudget =
    selectedPortfolioItem?.type === 'order'
      ? formatBudgetValue(selectedPortfolioItem.budget, selectedPortfolioItem.currency)
      : null;

  type QuickStat = {
    label: string;
    value: number | string;
    hint: string;
    icon: React.ComponentType<{ className?: string }>;
  };

  const quickStats: QuickStat[] = [
    {
      label: 'Visible projects',
      value: publicProjectsCount,
      hint:
        totalProjectsCount - publicProjectsCount > 0
          ? `${totalProjectsCount - publicProjectsCount} private project(s)`
          : 'All projects are visible',
      icon: FolderKanban,
    },
    {
      label: 'Shared orders',
      value: publicOrdersCount,
      hint: orders.length > 0 ? `${orders.length} order(s) total` : 'No orders recorded',
      icon: Calendar,
    },
    {
      label: 'Skills',
      value: skills.length,
      hint: skills.length > 0 ? 'Skills displayed publicly' : 'Add your skills',
      icon: Code,
    },
    {
      label: 'Awards',
      value: awards.length,
      hint: awards.length > 0 ? 'Awards highlighted' : 'No awards added',
      icon: Trophy,
    },
  ];

  type ProfileDetail = {
    label: string;
    value: string;
    icon: React.ComponentType<{ className?: string }>;
  };

  const profileDetails: ProfileDetail[] = [
    profile.location
      ? { label: 'Location', value: profile.location, icon: MapPin }
      : null,
    profile.show_email && (profile.email || profile.contact_email)
      ? {
          label: 'Email',
          value: profile.contact_email || profile.email || '',
          icon: Mail,
        }
      : null,
    profile.show_phone && profile.contact_phone
      ? {
          label: 'Phone',
          value: profile.contact_phone,
          icon: Phone,
        }
      : null,
    profile.created_at
      ? {
          label: 'Member since',
          value: new Date(profile.created_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
          }),
          icon: (props) => <Calendar {...props} />,
        }
      : null,
    subscriptionDate
      ? {
          label: 'Subscription',
          value: `For ${getTimeSinceSubscription()}`,
          icon: (props) => <SubscriberBadgeIcon {...props} />,
        }
      : null,
    {
      label: 'Visibility',
      value: isProfilePublic ? 'Public profile' : 'Private profile',
      icon: (props) => <PrivateBadgeIcon {...props} />,
    },
  ].filter((detail): detail is ProfileDetail => Boolean(detail));

  const handleTogglePortfolioVisibility = async (item: PortfolioItem) => {
    if (!isOwnProfile) return;

    const key = `${item.type}:${item.id}`;
    setVisibilityBusyMap((prev) => ({ ...prev, [key]: true }));

    try {
      const nextVisibility = !item.isPublic;

      if (item.type === 'project') {
        const updated = await ProjectsService.updateProject(item.id, { is_public: nextVisibility });
        setProjects((prev) => prev.map((project) => (project.id === updated.id ? updated : project)));
        setSelectedPortfolioItem((prev) =>
          prev && prev.id === item.id ? { ...prev, isPublic: updated.is_public } : prev
        );
      } else {
        const success = await OrdersService.updateOrderVisibility(item.id, nextVisibility);
        if (success) {
          setOrders((prev) =>
            prev.map((order) => (order.id === item.id ? { ...order, is_public: nextVisibility } : order))
          );
          setSelectedPortfolioItem((prev) =>
            prev && prev.id === item.id ? { ...prev, isPublic: nextVisibility } : prev
          );
        }
      }
    } catch (toggleError) {
      console.error('Error updating visibility:', toggleError);
    } finally {
      setVisibilityBusyMap((prev) => {
        const { [key]: _discarded, ...rest } = prev;
        return rest;
      });
    }
  };

  const renderPortfolioItem = (item: PortfolioItem) => {
    const key = `${item.type}:${item.id}`;
    const visibilityLabel = item.isPublic ? 'Visible publicly' : 'Hidden';

    const orderStatusMeta = item.type === 'order' ? getOrderStatusMeta(item.status) : null;
    const formattedBudget = item.type === 'order' ? formatBudgetValue(item.budget, item.currency) : null;

    return (
      <article
        key={key}
        role="button"
        tabIndex={0}
        onClick={() => setSelectedPortfolioItem(item)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setSelectedPortfolioItem(item);
          }
        }}
        className="rounded-lg border theme-card-border theme-card-bg p-4 transition hover:border-[rgb(var(--brand))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--brand))]"
      >
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-base font-semibold theme-text-primary">{item.title}</h3>
            <span className="text-xs font-medium uppercase theme-text-tertiary">
              {item.type === 'project' ? 'Project' : 'Order'}
            </span>
          </div>

          {item.description && (
            <p className="text-sm theme-text-secondary line-clamp-3">{item.description}</p>
          )}

          <div className="flex flex-wrap items-center gap-3 text-xs theme-text-secondary">
            <span className="inline-flex items-center gap-2 rounded-full border theme-card-border px-2 py-1">
              {item.isPublic ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
              {visibilityLabel}
            </span>

            <span className="inline-flex items-center gap-2">
              <Calendar className="h-3.5 w-3.5" />
              {formatPortfolioDate(item.createdAt)}
            </span>

            {item.type === 'order' && orderStatusMeta && (
              <span className="inline-flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${orderStatusMeta.dot}`} />
                {orderStatusMeta.label}
              </span>
            )}

            {item.type === 'order' && item.dueDate && (
              <span className="inline-flex items-center gap-2">
                <Calendar className="h-3.5 w-3.5" />
                Due {formatPortfolioDate(item.dueDate)}
              </span>
            )}

            {formattedBudget && <span className="font-medium theme-text-primary">{formattedBudget}</span>}
          </div>

          {item.type === 'project' && item.tags && item.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {item.tags.slice(0, 5).map((tag, index) => (
                <span key={index} className="rounded-full bg-[rgb(var(--card))] px-2 py-1 text-xs theme-text-tertiary">
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </article>
    );
  };

  return (
    <div className="page-stack space-y-8">
      <section className="rounded-xl border theme-card-border bg-[rgb(var(--card))] p-6">
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="flex flex-col items-center gap-4 md:items-start">
            <div className="h-24 w-24 overflow-hidden rounded-full border theme-card-border bg-[rgb(var(--card))] shadow-sm">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={profile.username || 'User avatar'}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="grid h-full w-full place-items-center text-3xl font-semibold theme-text-primary">
                  {(profile.username || profile.full_name || 'U').charAt(0).toUpperCase()}
                </span>
              )}
            </div>
          </div>

          <div className="flex-1 space-y-3">
            <div>
              <h1 className="text-2xl font-semibold theme-text-primary md:text-3xl">
                {profile.full_name || profile.username}
              </h1>
              {profile.username && <p className="text-sm theme-text-secondary">@{profile.username}</p>}
              {profile.professional_title && (
                <p className="text-sm theme-text-secondary">{profile.professional_title}</p>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
              {isOwnProfile && (
                <span className="inline-flex items-center gap-2 rounded-full border theme-card-border px-3 py-1 text-[rgb(var(--brand))]">
                  <User className="h-3.5 w-3.5" />
                  You
                </span>
              )}
              {(isAdmin || (isOwnProfile && currentUserIsAdmin)) && (
                <span className="inline-flex items-center gap-2 rounded-full border border-red-200 px-3 py-1 text-red-600 dark:border-red-500/40 dark:text-red-200">
                  <AdminBadgeIcon className="h-3.5 w-3.5" />
                  Admin
                </span>
              )}
              {hasActiveSubscription && (
                <span className="inline-flex items-center gap-2 rounded-full border border-purple-200 px-3 py-1 text-purple-600 dark:border-purple-500/40 dark:text-purple-200">
                  <SubscriberBadgeIcon className="h-3.5 w-3.5" />
                  {activeSubscription?.plan_display_name || 'Subscriber'}
                </span>
              )}
              {isPartner && (
                <span className="inline-flex items-center gap-2 rounded-full border border-amber-200 px-3 py-1 text-amber-600 dark:border-amber-500/40 dark:text-amber-200">
                  <PartnerBadgeIcon className="h-3.5 w-3.5" />
                  Partner
                </span>
              )}
              {!isProfilePublic && (
                <span className="inline-flex items-center gap-2 rounded-full border border-amber-200 px-3 py-1 text-amber-600 dark:border-amber-500/40 dark:text-amber-200">
                  <PrivateBadgeIcon className="h-3.5 w-3.5" />
                  Private profile
                </span>
              )}
            </div>

            {profile.bio && (
              <p className="max-w-2xl text-sm leading-relaxed theme-text-secondary">{profile.bio}</p>
            )}

            {socialLinks.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {socialLinks.map((link, index) => {
                  const Icon = link.icon;
                  return (
                    <a
                      key={index}
                      href={link.url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-full border theme-card-border px-3 py-2 text-xs font-medium theme-text-secondary transition hover:bg-[rgb(var(--card))]"
                    >
                      <Icon className="h-4 w-4" />
                      <span>{link.label}</span>
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 sm:items-end">
            {subscriptionDate && (
              <span className="rounded-full border theme-card-border px-3 py-1 text-xs font-medium theme-text-secondary">
                Subscribed for {getTimeSinceSubscription()}
              </span>
            )}
            {isOwnProfile && (
              <button
                onClick={() => navigate('/settings')}
                className="inline-flex items-center gap-2 rounded-full border theme-btn-primary px-4 py-2 text-sm font-medium transition hover:scale-[1.02]"
              >
                <Settings className="h-4 w-4" />
                Go to settings
              </button>
            )}
          </div>
        </div>

        {profileDetails.length > 0 && (
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {profileDetails.map(({ label, value, icon: DetailIcon }) => (
              <div key={label} className="flex items-center gap-3 rounded-lg border theme-card-border p-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg border theme-card-border bg-[rgb(var(--card))] text-[rgb(var(--brand))]">
                  <DetailIcon className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide theme-text-tertiary">{label}</p>
                  <p className="text-sm font-medium theme-text-primary">{value}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="rounded-xl border theme-card-border bg-[rgb(var(--card))] p-6">
        <h2 className="text-sm font-semibold theme-text-primary">Overview</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          {quickStats.map(({ label, value, hint, icon: IconStat }) => (
            <div key={label} className="rounded-lg border theme-card-border p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg border theme-card-border bg-[rgb(var(--card))] text-[rgb(var(--brand))]">
                  <IconStat className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide theme-text-tertiary">{label}</p>
                  <p className="text-lg font-semibold theme-text-primary">{value}</p>
                  <p className="text-xs theme-text-secondary">{hint}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {canViewPortfolio && (
        <section className="rounded-xl border theme-card-border bg-[rgb(var(--card))] p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-sm font-semibold theme-text-primary">Portfolio</h2>
              <p className="text-sm theme-text-secondary">{portfolioItems.length} shared item(s)</p>
            </div>
            {portfolioItems.length > 0 && (
              <button
                onClick={() => setIsPortfolioCollapsed((prev) => !prev)}
                className="inline-flex items-center gap-2 rounded-full border theme-btn-secondary px-4 py-2 text-xs font-medium transition hover:scale-[1.02]"
              >
                {isPortfolioCollapsed ? (
                  <>
                    <ChevronRight className="h-4 w-4" />
                    Show list
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    Hide list
                  </>
                )}
              </button>
            )}
          </div>

          {portfolioItems.length > 0 ? (
            isPortfolioCollapsed ? (
              <div className="mt-6 rounded-lg border theme-card-border p-6 text-center text-sm theme-text-secondary">
                List hidden. Expand the section to view shared items.
              </div>
            ) : (
              <div className="mt-6 space-y-4">{portfolioItems.map(renderPortfolioItem)}</div>
            )
          ) : (
            <div className="mt-6 flex flex-col items-center gap-3 rounded-lg border theme-card-border p-6 text-center">
              <FolderKanban className="h-10 w-10 text-[rgb(var(--brand))]" />
              <p className="text-sm theme-text-secondary">
                {isOwnProfile
                  ? 'Your portfolio is empty for now. Make your projects or orders visible to display them here.'
                  : "This portfolio doesn't display any public projects at the moment."}
              </p>
              {isOwnProfile && (
                <button
                  onClick={() => navigate('/settings?category=projects')}
                  className="inline-flex items-center gap-2 rounded-full border theme-btn-primary px-4 py-2 text-xs font-medium transition hover:scale-[1.02]"
                >
                  <Settings className="h-4 w-4" />
                  Manage my projects
                </button>
              )}
            </div>
          )}
        </section>
      )}

      {!canViewPortfolio && !isOwnProfile && (
        <section className="rounded-xl border theme-card-border bg-[rgb(var(--card))] p-6 text-center">
          <div className="mx-auto flex max-w-md flex-col items-center gap-4">
            <PrivateBadgeIcon className="h-10 w-10 text-amber-500" />
            <h2 className="text-base font-semibold theme-text-primary">Private portfolio</h2>
            <p className="text-sm theme-text-secondary">This user is keeping their projects confidential for now.</p>
          </div>
        </section>
      )}

      {skills.length > 0 && (
        <section className="rounded-xl border theme-card-border bg-[rgb(var(--card))] p-6">
          <h2 className="text-sm font-semibold theme-text-primary">Skills</h2>
          <div className="mt-4 space-y-3">
            {skills.map((skill) => (
              <div key={skill.id} className="rounded-lg border theme-card-border p-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold theme-text-primary">{skill.name}</h3>
                  <span className={`rounded-full px-2 py-1 text-[11px] font-semibold uppercase tracking-wide ${getLevelColor(skill.level)}`}>
                    {skill.level}
                  </span>
                </div>
                {skill.category && <p className="text-xs theme-text-secondary">{skill.category}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {awards.length > 0 && (
        <section className="rounded-xl border theme-card-border bg-[rgb(var(--card))] p-6">
          <h2 className="text-sm font-semibold theme-text-primary">Awards</h2>
          <div className="mt-4 space-y-3">
            {awards.map((award) => (
              <div key={award.id} className="flex gap-3 rounded-lg border theme-card-border p-4">
                {award.image_url ? (
                  <img src={award.image_url} alt={award.title} className="h-14 w-14 flex-shrink-0 rounded-lg object-cover" />
                ) : (
                  <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-lg border theme-card-border bg-amber-500/10 text-amber-500">
                    <AwardIcon className="h-7 w-7" />
                  </div>
                )}
                <div className="space-y-1">
                  <h3 className="text-sm font-semibold theme-text-primary">{award.title}</h3>
                  <p className="text-xs theme-text-secondary">{award.issuer}</p>
                  {award.description && <p className="text-sm theme-text-secondary">{award.description}</p>}
                  <p className="text-xs theme-text-tertiary">
                    {new Date(award.date_received).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {selectedPortfolioItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-10">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSelectedPortfolioItem(null)} aria-hidden="true" />
          <div className="relative z-10 w-full max-w-2xl rounded-xl border theme-card-border bg-[rgb(var(--card))] p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-2">
                <span className="text-xs font-semibold uppercase theme-text-tertiary">
                  {selectedPortfolioItem.type === 'project' ? 'Portfolio project' : 'Shared order'}
                </span>
                <h3 className="text-xl font-semibold theme-text-primary">{selectedPortfolioItem.title}</h3>
                <p className="text-xs theme-text-tertiary">Added on {formatPortfolioDate(selectedPortfolioItem.createdAt)}</p>
              </div>
              <button
                onClick={() => setSelectedPortfolioItem(null)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border theme-btn-secondary"
                title="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {selectedPortfolioItem.description && (
              <p className="mt-4 text-sm leading-relaxed theme-text-secondary">{selectedPortfolioItem.description}</p>
            )}

            {selectedPortfolioItem.tags && selectedPortfolioItem.tags.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {selectedPortfolioItem.tags.map((tag, index) => (
                  <span key={`${tag}-${index}`} className="rounded-full border theme-card-border px-2 py-1 text-xs theme-text-tertiary">
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {selectedPortfolioItem.type === 'order' && (
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {selectedOrderMeta && (
                  <div className="rounded-lg border theme-card-border p-3 text-sm theme-text-secondary">
                    <span className="font-semibold theme-text-primary">Status:</span>{' '}
                    <span className="inline-flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full ${selectedOrderMeta.dot}`} />
                      {selectedOrderMeta.label}
                    </span>
                  </div>
                )}
                {selectedPortfolioItem.dueDate && (
                  <div className="rounded-lg border theme-card-border p-3 text-sm theme-text-secondary">
                    <span className="font-semibold theme-text-primary">Delivery:</span>{' '}
                    {formatPortfolioDate(selectedPortfolioItem.dueDate)}
                  </div>
                )}
                {selectedOrderBudget && (
                  <div className="rounded-lg border theme-card-border p-3 text-sm theme-text-secondary">
                    <span className="font-semibold theme-text-primary">Budget:</span>{' '}
                    {selectedOrderBudget}
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm theme-text-secondary">
              <span className="inline-flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Created on {formatPortfolioDate(selectedPortfolioItem.createdAt)}
              </span>
              <span className="inline-flex items-center gap-2">
                {selectedPortfolioItem.isPublic ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                {selectedPortfolioItem.isPublic ? 'Visible publicly' : 'Hidden from visitors'}
              </span>
            </div>

            {isOwnProfile && (
              <div className="mt-6 flex items-center justify-between gap-3 rounded-lg border theme-card-border p-3">
                <p className="text-sm theme-text-secondary">
                  {selectedPortfolioItem.isPublic
                    ? 'Hide this content if you want to keep it private.'
                    : 'Publish this content to make it visible on your profile.'}
                </p>
                <button
                  onClick={() => handleTogglePortfolioVisibility(selectedPortfolioItem)}
                  disabled={selectedItemBusy}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border theme-btn-secondary disabled:opacity-60"
                  title={selectedPortfolioItem.isPublic ? 'Hide this project/order' : 'Publish this project/order'}
                >
                  {selectedItemBusy ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : selectedPortfolioItem.isPublic ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PageProfile;